import React, { FC, useEffect, useState } from 'react';
import { FormProvider, SubmitErrorHandler, useForm } from 'react-hook-form';
import { yupResolver } from '@hookform/resolvers/yup';
import { useRouter } from 'next/router';
import { useToast } from '@common/toast';
import BackgroundArea from '@components/profile/profileOverlay/overlays/common/backgroundArea';
import PreferTab from '@components/profile/profileOverlay/overlays/prefer/PreferTab';
import PreferTabTitle from '@components/profile/profileOverlay/overlays/prefer/PreferTabTitle';
import JobGroup from '@components/profile/profileOverlay/overlays/prefer/JobGroup';
import Location from '@components/profile/profileOverlay/overlays/prefer/Location';
import Salary from '@components/profile/profileOverlay/overlays/prefer/Salary';
import Welfare from '@components/profile/profileOverlay/overlays/prefer/Welfare';
import ExitModal from '@components/profile/profileOverlay/exitModal';
import { useUserInfo } from '@repository/auth/useUserInfo';
import useProfileConditions, {
  keys as profileConditionKeys,
  useFetchSaveProfileConditions,
} from '@repository/profiles/useProfileConditions';
import { keys as profilesTypeKey } from '@repository/profiles/useAccaPhsInfo';
import profileConditionsSchema from './profileConditionsSchema';
import ProfileConditionsFormVO from '@domain/vo/profile/ProfileConditionsFormVO';
import { queryClient } from '@utils/queryClient';

import classnames from 'classnames/bind';
import styles from './prefer.module.scss';
const cx = classnames.bind(styles);

interface IPreferOverlay {
  visible: boolean;
  onClose: () => void;
}

export type PreferTabType = 'jobGroup' | 'location' | 'salary' | 'welfare';
export type PreferConditionsFormType = 'jobGroups' | 'locations' | 'salary' | 'welfareKeywords';

const preferDefaultValues: RecursivePartial<ProfileConditionsFormVO> = {
  jobGroups: [],
  locations: [],
  salary: null,
  welfareKeywords: [],
};

const PreferOverlay: FC<IPreferOverlay> = ({ visible, onClose }) => {
  const Toast = useToast();
  const { query } = useRouter();
  const [selectedTabType, setSelectedTabType] = useState<PreferTabType>('jobGroup');
  const [exitModal, setExitModal] = useState(false);

  const { data: userInfo } = useUserInfo();
  const { data: conditionsData, isLoading } = useProfileConditions(!!userInfo?.id && visible);

  const saveSuccess = async () => {
    await queryClient.refetchQueries([...profileConditionKeys, 'view'], { active: true, exact: true });
    //SNb 역량/PHS 정보 활성화 상태 업데이트
    await queryClient.refetchQueries(profilesTypeKey, { active: true, exact: true });

    onClose();
    Toast({ type: 'success', iconType: 'info', content: '성공적으로 저장되었습니다.' });
  };

  const { mutate: saveProfileConditions } = useFetchSaveProfileConditions(saveSuccess);

  const methods = useForm<ProfileConditionsFormVO>({
    mode: 'onChange',
    defaultValues: preferDefaultValues as ProfileConditionsFormVO,
    resolver: yupResolver(profileConditionsSchema) as any,
  });

  const {
    getValues,
    handleSubmit,
    reset,
    formState: { isDirty },
  } = methods;

  const onCloseOverlay = () => {
    const preferFields = getValues('jobGroups');
    /* 
      isDirty는 깊은 비교를 하지만 jobGroups의 데이터 형식인 [{string, array[]}]에서 array까지는 깊은 비교를 해주지 않아,
      같은 직군의 직무(array)를 직접 비교해주어야한다.
    */
    const isDirtyJobGroups = preferFields.some(
      ({ jobGroupCode, jobTitleCodes }: { jobGroupCode: any; jobTitleCodes: any }) =>
        conditionsData?.jobGroups.some(
          ({ jobGroupCode: conditionDataJobGroupCode, jobTitles: conditionDataJobTitles }) =>
            jobGroupCode === conditionDataJobGroupCode && jobTitleCodes.length !== conditionDataJobTitles.length,
        ),
    );

    if (isDirty || isDirtyJobGroups) {
      setExitModal(true);
      return;
    }

    handleExit();
  };

  const isSelectedTab = (preferTabType: PreferTabType) => preferTabType === selectedTabType;

  const handleExit = () => {
    setSelectedTabType('jobGroup');
    onClose();
  };

  const onSubmit = (vo: ProfileConditionsFormVO) =>
    saveProfileConditions(Object.assign(vo, { sourcePage: query.source_page }));

  const onSubmitError: SubmitErrorHandler<ProfileConditionsFormVO> = (errors) => {
    Toast({ type: 'error', iconType: 'info', content: '저장에 필요한 정보를 작성해주세요.' });
    const errorsKey = Object.keys(errors);

    if (errorsKey.includes('jobGroups')) {
      setSelectedTabType('jobGroup');
      return;
    }
    if (errorsKey.includes('locations')) {
      setSelectedTabType('location');
      return;
    }
    if (errorsKey.includes('salary')) {
      setSelectedTabType('salary');
      return;
    }
    if (errorsKey.includes('welfareKeywords')) {
      setSelectedTabType('welfare');
      return;
    }
  };

  useEffect(() => {
    if (conditionsData) reset(new ProfileConditionsFormVO(conditionsData));
  }, [visible, conditionsData]);

  if (!conditionsData || isLoading) return null;

  return (
    <>
      <BackgroundArea visible={visible} onClickArea={onCloseOverlay} />
      <FormProvider {...methods}>
        <form onSubmit={(v) => handleSubmit(onSubmit, onSubmitError)(v)}>
          <div className={cx('overlayArea', { on: visible })}>
            <div className={cx('overlayInner')}>
              <div className={cx('overlayTitleArea', { noGradient: !isSelectedTab('welfare') })}>
                {/* 상단 탭 영역 */}
                <PreferTab selectedTabType={selectedTabType} setSelectedTabType={setSelectedTabType} />

                {/* 탭에 따른 타이틀 영역 */}
                <PreferTabTitle selectedTabType={selectedTabType} />
              </div>

              <div className={cx('scrollArea')}>
                <div className={cx('scrollInner')}>
                  <div className={cx('scrollWrap', { welfare: isSelectedTab('welfare') })}>
                    {/* 직군, 직무 */}
                    <JobGroup name={'jobGroups'} visible={isSelectedTab('jobGroup')} />
                    {/* 근무 지역 */}
                    <Location name={'locations'} visible={isSelectedTab('location')} />
                    {/* 연봉 수준 */}
                    <Salary name={'salary'} visible={isSelectedTab('salary')} />
                    {/* 복지 */}
                    <Welfare name={'welfareKeywords'} visible={isSelectedTab('welfare')} />
                  </div>
                </div>
              </div>

              <div className={cx('btnArea')}>
                <a className={cx('btn', 'cancel')} role="button" onClick={onCloseOverlay}>
                  나가기
                </a>
                <button className={cx('btn', 'confirm')} type="submit">
                  저장
                </button>
              </div>
            </div>

            {/* 나가기 모달 영역 */}
            <ExitModal visible={exitModal} onClose={() => setExitModal(false)} onExit={handleExit} />
          </div>
        </form>
      </FormProvider>
    </>
  );
};

export default PreferOverlay;
